import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { bairroService } from "../services/enderecoService";
import { Bairro } from "../types/bairro";
import { Cidade } from "../types/cidade";
import { useDebounce } from "../hooks/useDebounce";

interface BairroSelectionModalProps {
  cidade: Cidade;
  isOpen: boolean;
  onClose: () => void;
  onSave: (bairros: Set<number>, bairrosNomes: Map<number, string>) => void;
  initialSelectedBairros: Set<number>;
}

export const BairroSelectionModal: React.FC<BairroSelectionModalProps> = ({
  cidade,
  isOpen,
  onClose,
  onSave,
  initialSelectedBairros,
}) => {
  const [allBairros, setAllBairros] = useState<Bairro[]>([]);
  const [filteredBairros, setFilteredBairros] = useState<Bairro[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBairros, setSelectedBairros] = useState<Set<number>>(
    initialSelectedBairros
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (!isOpen) return;
    const fetchBairros = async () => {
      setLoading(true);
      try {
        const options = await bairroService.listarPorMunicipio(cidade.id);
        const bairrosData: Bairro[] = options.map(opt => ({
          id: Number(opt.value),
          nome: opt.label.toLowerCase(),
          nome_original: opt.label,
          municipio: cidade,
          uf: cidade.uf, 
        }));
        setAllBairros(bairrosData);
        setFilteredBairros(bairrosData);
      } catch (err) {
        console.error("Erro ao carregar bairros:", err);
        toast.error("Erro ao carregar a lista de bairros.");
      } finally {
        setLoading(false);
      }
    };
    fetchBairros();
  }, [isOpen, cidade]);

  useEffect(() => {
    const lowercasedFilter = debouncedSearchTerm.toLowerCase();
    const filtered = allBairros.filter((bairro) =>
      bairro.nome_original.toLowerCase().includes(lowercasedFilter)
    );
    setFilteredBairros(filtered);
  }, [debouncedSearchTerm, allBairros]);

  const handleSelectBairro = (bairroId: number) => {
    setSelectedBairros((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(bairroId)) {
        newSet.delete(bairroId);
      } else {
        newSet.add(bairroId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedBairros.size === filteredBairros.length) {
      // Deselect all
      setSelectedBairros(new Set());
    } else {
      // Select all
      const allBairroIds = new Set(filteredBairros.map((b) => b.id));
      setSelectedBairros(allBairroIds);
    }
  };

  const handleSave = () => {
    const bairrosNomes = new Map<number, string>();
    selectedBairros.forEach((bairroId) => {
      const bairro = allBairros.find((b) => b.id === bairroId);
      if (bairro) {
        bairrosNomes.set(bairroId, bairro.nome_original);
      }
    });
    onSave(selectedBairros, bairrosNomes);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-6 border shadow-lg rounded-md bg-white w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            Selecionar Bairros de {cidade.nome_original}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
          >
            &times;
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nome do bairro..."
            className="flex-grow px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            {selectedBairros.size === filteredBairros.length
              ? "Desmarcar Todos"
              : "Marcar Todos"}
          </button>
        </div>

        {loading ? (
          <div className="flex-grow flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="ml-4 text-gray-600">Carregando bairros...</p>
          </div>
        ) : (
          <div className="flex-grow overflow-y-auto pr-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {filteredBairros.map((bairro) => (
                <label
                  key={bairro.id}
                  className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-150 ${
                    selectedBairros.has(bairro.id)
                      ? "bg-blue-100 border-blue-300 shadow-sm"
                      : "bg-white hover:bg-gray-100 border-gray-200"
                  } border`}
                  onClick={() => handleSelectBairro(bairro.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedBairros.has(bairro.id)}
                    readOnly
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-800">
                    {bairro.nome_original}
                  </span>
                </label>
              ))}
            </div>
            {filteredBairros.length === 0 && (
              <div className="text-center py-10">
                <p className="text-gray-500">
                  Nenhum bairro encontrado com o termo "{searchTerm}".
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end items-center mt-6 pt-4 border-t">
          <span className="text-sm text-gray-600 mr-4">
            {selectedBairros.size} de {filteredBairros.length} bairros
            selecionados
          </span>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md mr-3 hover:bg-gray-300"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};
